import type { ReactElement, ReactNode } from "react";
import { createPortal } from "react-dom";

import { cn } from "~/lib/utils";
import type { RestingComposerState } from "./useRestingComposer";

/**
 * The Composer's model, effort and runtime mode controls, rendered into the
 * controls strip below it while the Composer rests.
 *
 * A portal rather than a second copy of the cluster. The controls carry live
 * provider state and open menus that write to the Draft, and two mounted
 * instances would be two of everything: two menu open flags, two sets of
 * effects, and a real chance of the strip showing a stale model. One instance
 * that changes where it lands keeps a single owner of each choice.
 *
 * Hidden rather than unmounted when it does not fit. The layout decision is
 * made from the cluster's measured width, so unmounting it would remove the
 * thing the next measurement needs and the controls could never come back.
 */
export function RestingComposerControlsPortal({
  controlsHost,
  controlsRef,
  controlsVisible,
  children,
}: RestingComposerState & { readonly children: ReactNode }): ReactElement | null {
  if (!controlsHost) return null;
  return createPortal(
    <div
      ref={controlsRef}
      // Read by `isInsideRestingComposerControlScope`, which is what stops a
      // focus landing here from expanding the Composer out from under it.
      data-chat-composer-resting-controls="true"
      className={cn(
        "relative flex min-w-0 shrink items-center gap-1",
        controlsVisible ? "" : "pointer-events-none invisible",
      )}
    >
      {children}
    </div>,
    controlsHost,
  );
}
